// ══════════════════════════════════════════════════════════════
//  Orders.jsx
//  Lógica: 100% original del viejo componente
//  Visual:  shell del nuevo OrderCards (cards expandibles,
//           timeline, búsqueda, toast, tabs pill)
// ══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarMenu } from '../common/BarMenu/BarMenu';
import { Header } from '../common/Header/Header';
import InfoOrdersModal from './InfoOrdersModal';
import { PreOrderFetchAPI } from '../../api/PaymentPost/PreOrder';
import { OrdersFetchAPI } from '../../api/Orders';
import { TablesFetchAPI } from '../../api/Tables';
import { ClientsFetchAPI } from '../../api/Clients';
import moment from 'moment-timezone';
import Socket from '../../utils/Socket.config';
import ErrorAlert from '../Alerts/ErrorAlert';
import { RenderIf } from '../common/utils/RenderIf';
import LoginUser from '../Profile/LoginUser';
import SubMenuNav from '../Menu/SubMenu/SubMenuNav ';
import HeaderMain from '../Home/Headers/HeaderMain';
import Loader from '../Home/Loader';

import AlertSmall from '../Alerts/AlertSmall';
import OrdersCards from './components/OrdersCards';
import { clearClientSession, normalizeClient } from '../../utils/ownershipContext';
import { buildCloudinaryImageUrl } from '../../utils/CloudinaryImageUrl';

/* ══════════════════════════════════════════════════════════════
   CSS — inyectado una vez. Prefijo: .ord-
   Combina el sistema del nuevo (cards, timeline, toast, tabs)
   con los tokens del design system existente.
══════════════════════════════════════════════════════════════ */
const CSS = `
@keyframes ord-in   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes ord-open { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
@keyframes ord-spin { to{transform:rotate(360deg)} }
@keyframes ord-toast{ 0%{opacity:0;transform:translateX(-50%) translateY(12px)} 10%,90%{opacity:1;transform:translateX(-50%) translateY(0)} 100%{opacity:0;transform:translateX(-50%) translateY(-8px)} }

/* ── page ── */
.ord-page {
  min-height: 100vh;
  background: var(--bg-3, #F8F8F8);
  padding-bottom: 5rem;
}

/* ── page header ── */
.ord-ph {
  background: var(--bg, #fff);
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
  padding: 1rem 0 .85rem;
  margin-bottom: 1.25rem;
}
.ord-ph-inner {
  max-width: 1060px; margin: 0 auto;
  padding: 0 clamp(1rem,3vw,2rem);
  display: flex; align-items: flex-end;
  justify-content: space-between; gap: 1rem;
  flex-wrap: wrap;
}
.ord-ph-left {}
.ord-ph-eyebrow {
  font-size: .68rem; font-weight: 700;
  letter-spacing: .09em; text-transform: uppercase;
  color: var(--brand, --color-primary);
  margin-bottom: .18rem;
}
.ord-ph-title {
  font-size: 1.25rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  letter-spacing: -.03em; line-height: 1.2;
}
.ord-ph-sub {
  font-size: .77rem; color: var(--text-3, #B4B4B4);
  margin-top: .15rem;
}

/* search */
.ord-search {
  display: flex; align-items: center; gap: .5rem;
  padding: .44rem .85rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 999px;
  background: var(--bg-2, rgba(0,0,0,.035));
  transition: border-color .15s, box-shadow .15s;
}
.ord-search:focus-within {
  border-color: var(--brand, --color-primary);
  box-shadow: 0 0 0 3px rgba(242,101,34,.1);
  background: var(--bg, #fff);
}
.ord-search svg { color: var(--text-3, #B4B4B4); flex-shrink: 0; }
.ord-search input {
  border: none; background: transparent; outline: none;
  font-size: .82rem; color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  width: 170px; min-width: 0;
}
.ord-search input::placeholder { color: var(--text-3, #B4B4B4); }
@media(max-width:480px){ .ord-search input{ width:120px } }

/* ── tabs ── */
.ord-tabs-wrap {
  display: flex; gap: .3rem;
  overflow-x: auto; scrollbar-width: none;
  padding: 0 clamp(1rem,3vw,2rem);
  max-width: 1060px; margin: 0 auto 1.25rem;
}
.ord-tabs-wrap::-webkit-scrollbar { display: none; }
.ord-tab {
  display: inline-flex; align-items: center; gap: .38rem;
  padding: .38rem .9rem;
  border-radius: 999px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg, #fff);
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .78rem; font-weight: 500;
  cursor: pointer; white-space: nowrap;
  flex-shrink: 0;
  transition: all .14s;
}
.ord-tab:hover { border-color: var(--brand, --color-primary); color: var(--brand, --color-primary); background: var(--brand-dim, rgba(242,101,34,.07)); }
.ord-tab.active { background: var(--brand, --color-primary); border-color: var(--brand, --color-primary); color: #fff; font-weight: 600; }
.ord-tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; border-radius: 5px;
  background: rgba(0,0,0,.08); color: inherit;
  font-size: .63rem; font-weight: 700; padding: 0 .25rem;
}
.ord-tab.active .ord-tab-count { background: rgba(255,255,255,.22); }

/* ── results meta ── */
.ord-meta {
  max-width: 1060px; margin: 0 auto;
  padding: 0 clamp(1rem,3vw,2rem);
  font-size: .75rem; color: var(--text-3, #B4B4B4);
  margin-bottom: .75rem;
}

/* ── list ── */
.ord-list {
  max-width: 1060px; margin: 0 auto;
  padding: 0 clamp(1rem,3vw,2rem);
  display: flex; flex-direction: column; gap: .65rem;
}

/* ── empty ── */
.ord-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 4rem 1rem; text-align: center;
  animation: ord-in .3s ease both;
}
.ord-empty-ico {
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--bg-2, rgba(0,0,0,.04));
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  display: grid; place-items: center;
  font-size: 1.7rem; margin-bottom: .75rem;
}
.ord-empty-title { font-size: .92rem; font-weight: 600; color: var(--text, #0A0A0A); margin: 0 0 .3rem; }
.ord-empty-sub   { font-size: .78rem; color: var(--text-3, #B4B4B4); margin: 0 0 1rem; }
.ord-empty-btn {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .6rem 1.25rem;
  border-radius: 999px; border: none;
  background: var(--brand, --color-primary); color: #fff;
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .82rem; font-weight: 600;
  cursor: pointer; transition: background .15s, transform .15s;
}
.ord-empty-btn:hover { background: var(--brand-hover, #D4521A); transform: translateY(-1px); }

/* ══ ORDER CARD ══ */
.ord-card {
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow .16s, border-color .16s;
  animation: ord-in .25s ease both;
  position: relative;
}
.ord-card:hover { box-shadow: 0 6px 24px rgba(0,0,0,.08); border-color: var(--border-md, rgba(0,0,0,.11)); }

/* color stripe */
.ord-card-bar { height: 3px; flex-shrink: 0; }
.ord-card-bar.facturado { background: #22C55E; }
.ord-card-bar.pendiente { background: #F59E0B; }
.ord-card-bar.listo     { background: #22C55E; }
.ord-card-bar.anulado   { background: #EF4444; }
.ord-card-bar.review    { background: var(--brand, --color-primary); }

/* card inner row */
.ord-card-inner {
  display: flex; align-items: center; gap: 1rem;
  padding: .9rem 1.1rem;
}
@media(max-width:560px){
  .ord-card-inner { flex-wrap: wrap; }
}

/* thumb */
.ord-thumb {
  width: 58px; height: 58px; border-radius: 10px;
  overflow: hidden; flex-shrink: 0; position: relative;
  background: var(--bg-2, rgba(0,0,0,.04));
  border: 1px solid var(--border, rgba(0,0,0,.07));
}
.ord-thumb img { width: 100%; height: 100%; object-fit: cover; }
.ord-thumb-count {
  position: absolute; bottom: 2px; right: 2px;
  background: rgba(0,0,0,.55); color: #fff;
  font-size: .58rem; font-weight: 700;
  padding: 1px 4px; border-radius: 4px;
}
/* fallback thumb cuando no hay imagen */
.ord-thumb-ph {
  width: 58px; height: 58px; border-radius: 10px; flex-shrink: 0;
  background: var(--brand-dim, rgba(242,101,34,.09));
  border: 1px solid rgba(242,101,34,.16);
  display: grid; place-items: center;
  color: var(--brand, --color-primary); font-size: 1.3rem;
}

/* info */
.ord-info { flex: 1; min-width: 0; }
.ord-info-header { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; margin-bottom: .22rem; }
.ord-info-id { font-size: .78rem; font-weight: 700; color: var(--text, #0A0A0A); }
.ord-status-pill {
  display: inline-flex; align-items: center; gap: .28rem;
  padding: .15rem .55rem;
  border-radius: 999px;
  font-size: .65rem; font-weight: 700;
}
.ord-status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.ord-status-pill.facturado { background: rgba(34,197,94,.12);  color: #15803D; }
.ord-status-pill.pendiente { background: rgba(245,158,11,.12); color: #92400E; }
.ord-status-pill.listo     { background: rgba(34,197,94,.12);  color: #15803D; }
.ord-status-pill.anulado   { background: rgba(239,68,68,.10);  color: #B91C1C; }
.ord-status-pill.review    { background: var(--brand-dim, rgba(242,101,34,.1)); color: var(--brand, --color-primary); }
[data-theme="dark"] .ord-status-pill.facturado { background: rgba(34,197,94,.15);  color: #4ADE80; }
[data-theme="dark"] .ord-status-pill.pendiente { background: rgba(245,158,11,.15); color: #FCD34D; }
[data-theme="dark"] .ord-status-pill.listo     { background: rgba(34,197,94,.15);  color: #4ADE80; }
[data-theme="dark"] .ord-status-pill.anulado   { background: rgba(239,68,68,.12);  color: #FCA5A5; }

.ord-info-name { font-size: .84rem; font-weight: 600; color: var(--text, #0A0A0A); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: .25rem; }
.ord-info-meta { display: flex; flex-wrap: wrap; gap: .55rem; }
.ord-meta-chip {
  display: inline-flex; align-items: center; gap: .28rem;
  font-size: .71rem; color: var(--text-3, #B4B4B4);
}

/* right */
.ord-right { display: flex; flex-direction: column; align-items: flex-end; gap: .45rem; flex-shrink: 0; }
.ord-price     { font-size: 1rem; font-weight: 700; color: var(--text, #0A0A0A); letter-spacing: -.02em; }
.ord-price-tax { font-size: .64rem; color: var(--text-3, #B4B4B4); text-align: right; margin-top: -.2rem; }
.ord-toggle-btn {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .3rem .7rem;
  border-radius: 8px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .75rem; font-weight: 500;
  cursor: pointer; transition: all .14s; white-space: nowrap;
}
.ord-toggle-btn:hover { border-color: var(--brand, --color-primary); color: var(--brand, --color-primary); background: var(--brand-dim, rgba(242,101,34,.07)); }
.ord-toggle-btn.open  { border-color: var(--brand, --color-primary); color: var(--brand, --color-primary); background: var(--brand-dim, rgba(242,101,34,.07)); }
.ord-toggle-btn svg   { transition: transform .25s; }
.ord-toggle-btn.open svg { transform: rotate(180deg); }

/* ══ DETAIL PANEL ══ */
.ord-detail { display: none; }
.ord-detail.open {
  display: block;
  border-top: 1px solid var(--border, rgba(0,0,0,.07));
  animation: ord-open .2s ease both;
}
.ord-detail-inner {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 1.5rem; padding: 1.1rem 1.1rem 1.25rem;
}
@media(max-width:640px){ .ord-detail-inner{ grid-template-columns: 1fr; gap: 1rem; } }

.ord-detail-title {
  font-size: .68rem; font-weight: 700;
  letter-spacing: .08em; text-transform: uppercase;
  color: var(--text-3, #B4B4B4);
  margin-bottom: .65rem;
  padding-bottom: .4rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}

/* items */
.ord-items { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 1.1rem; }
.ord-item {
  display: flex; align-items: center; gap: .65rem;
  padding: .55rem .65rem;
  border-radius: 10px;
  background: var(--bg-2, rgba(0,0,0,.035));
  transition: background .13s;
}
.ord-item:hover { background: rgba(0,0,0,.055); }
.ord-item-img {
  width: 42px; height: 42px; border-radius: 8px;
  overflow: hidden; flex-shrink: 0;
  background: var(--border, rgba(0,0,0,.07));
}
.ord-item-img img { width: 100%; height: 100%; object-fit: cover; }
.ord-item-body { flex: 1; min-width: 0; }
.ord-item-name { font-size: .8rem; font-weight: 600; color: var(--text, #0A0A0A); }
.ord-item-desc { font-size: .7rem; color: var(--text-3, #B4B4B4); margin-top: 1px; }
.ord-item-qty  { font-size: .76rem; color: var(--text-3, #B4B4B4); flex-shrink: 0; }
.ord-item-price{ font-size: .82rem; font-weight: 700; color: var(--brand, --color-primary); flex-shrink: 0; }

/* timeline */
.ord-timeline { display: flex; flex-direction: column; gap: 0; }
.ord-tl-step {
  display: flex; gap: .75rem;
  padding-bottom: .8rem;
  position: relative;
}
.ord-tl-step:not(:last-child)::before {
  content: '';
  position: absolute; left: 10px; top: 22px;
  width: 1px; bottom: 0;
  background: var(--border, rgba(0,0,0,.07));
}
.ord-tl-dot {
  width: 21px; height: 21px; border-radius: 50%; flex-shrink: 0;
  display: grid; place-items: center;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.12));
  background: var(--bg, #fff);
  color: var(--text-3, #B4B4B4);
  z-index: 1;
}
.ord-tl-step.done    .ord-tl-dot { border-color: #22C55E; background: #22C55E; color: #fff; }
.ord-tl-step.current .ord-tl-dot { border-color: var(--brand, --color-primary); background: var(--brand-dim, rgba(242,101,34,.1)); color: var(--brand, --color-primary); }
.ord-tl-label { font-size: .8rem; font-weight: 600; color: var(--text, #0A0A0A); }
.ord-tl-time  { font-size: .7rem; color: var(--text-3, #B4B4B4); margin-top: 1px; }
.ord-tl-step.pending-step .ord-tl-label { color: var(--text-3, #B4B4B4); font-weight: 400; }

/* summary */
.ord-summary {
  background: var(--bg-2, rgba(0,0,0,.035));
  border-radius: 12px; padding: .85rem 1rem;
  margin-bottom: .85rem;
}
.ord-sum-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: .8rem; color: var(--text-2, #6B6B6B); margin-bottom: .22rem;
}
.ord-sum-row.total { padding-top: .4rem; border-top: 1px solid var(--border, rgba(0,0,0,.07)); font-weight: 700; color: var(--text, #0A0A0A); margin-bottom: 0; }
.ord-sum-row.total span:last-child { color: var(--brand, --color-primary); font-size: .92rem; }
.ord-sum-brand { color: #22C55E; font-weight: 600; }

/* address */
.ord-addr-box {
  background: var(--bg-2, rgba(0,0,0,.035));
  border-radius: 10px; padding: .7rem .85rem;
  font-size: .78rem; color: var(--text-2, #6B6B6B);
  line-height: 1.5; margin-bottom: .85rem;
}
.ord-addr-label { font-size: .66rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text-3, #B4B4B4); margin-bottom: .25rem; }

/* detail actions */
.ord-detail-actions { display: flex; gap: .45rem; flex-wrap: wrap; }
.ord-btn-reorder {
  flex: 1; min-width: 130px;
  display: flex; align-items: center; justify-content: center; gap: .35rem;
  padding: .6rem .85rem;
  border-radius: 10px; border: none;
  background: var(--brand, --color-primary); color: #fff;
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .79rem; font-weight: 600;
  cursor: pointer; transition: background .14s, transform .14s;
}
.ord-btn-reorder:hover { background: var(--brand-hover, #D4521A); transform: translateY(-1px); }
.ord-btn-invoice {
  flex: 1; min-width: 130px;
  display: flex; align-items: center; justify-content: center; gap: .35rem;
  padding: .6rem .85rem;
  border-radius: 10px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .79rem; font-weight: 500;
  cursor: pointer; transition: all .14s;
}
.ord-btn-invoice:hover { border-color: var(--text-2, #6B6B6B); color: var(--text, #0A0A0A); }

/* ══ TOAST ══ */
.ord-toast {
  position: fixed; bottom: 24px; left: 50%; z-index: 2000;
  transform: translateX(-50%) translateY(0);
  background: rgba(10,10,10,.88);
  color: #fff;
  backdrop-filter: blur(8px);
  padding: .55rem 1.1rem;
  border-radius: 999px;
  font-size: .8rem; font-weight: 500;
  display: flex; align-items: center; gap: .45rem;
  pointer-events: none;
  animation: ord-toast 2.8s ease both;
}
.ord-toast-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--brand, --color-primary); flex-shrink: 0; }

/* ══ REFRESH BTN ══ */
.ord-refresh {
  display: inline-flex; align-items: center; gap: .38rem;
  padding: .36rem .85rem;
  border-radius: 8px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg-2, rgba(0,0,0,.035));
  color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .78rem; font-weight: 500;
  cursor: pointer; transition: all .15s;
  margin-top: .45rem;
}
.ord-refresh:hover { border-color: var(--brand, --color-primary); color: var(--brand, --color-primary); }
.ord-refresh:hover svg { animation: ord-spin .5s linear; }
`;

/* ══════════════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════════════ */
const IcoSearch = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
const IcoChevron = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>;
const IcoCalendar = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
const IcoPin = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const IcoBox2 = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /></svg>;
const IcoCheck = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>;
const IcoDot = () => <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="6" /></svg>;
const IcoReorder = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>;
const IcoDownload = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></svg>;
const IcoArrow = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>;
const IcoReceipt = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>;
const IcoRefresh = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>;
const IcoOrders = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>;

const fmt = (n) => '$' + (n || 0).toFixed(2);

/* ── mapeo de datos de la API al formato de card ── */
const statusFromOrder = (order, tab) => {
   if (tab === 'catogire1') return 'facturado';
   if (tab === 'catogire2') return order.is_authorized === false ? 'review' : 'pendiente';
   if (tab === 'catogire3') return 'listo';
   if (tab === 'catogire4') return 'anulado';
   return 'pendiente';
};

const statusLabel = (s, t) => ({
   facturado: t('orders.statusInvoiced'), pendiente: t('orders.statusPending'),
   listo: t('orders.statusReady'), anulado: t('orders.statusCancelled'), review: t('orders.statusReview'),
}[s] || s);

/* ── Toast ── */
function Toast({ message, visible }) {
   if (!visible) return null;
   return (
      <div className="ord-toast">
         <span className="ord-toast-dot" />
         {message}
      </div>
   );
}

/* ── OrderCard ── */
function OrderCard({ order, tab, isOpen, onToggle, onNavigate, onShowModal, onToast }) {
   const { t } = useTranslation();
   const status = statusFromOrder(order, tab);
   // ⚠️ FIX: usar _id como identificador único — transaction_code y code
   // pueden estar vacíos o repetidos entre tabs, causando que todas las
   // cards con el mismo código se abran/cierren a la vez.
   const uid = order._id || order.transaction_code || order.code || String(Math.random());
   const code = order.transaction_code || order.code || '—';
   const name = order.name || (order.groups?.map(g => g.name).join(', ')) || 'Orden';

   return (
      <div className="ord-card" id={`ord-${uid}`}>
         <div className={`ord-card-bar ${status}`} />

         <div className="ord-card-inner">
            {/* thumb — placeholder con ícono cuando no hay img */}
            <div className="ord-thumb-ph"><IcoReceipt /></div>

            {/* info */}
            <div className="ord-info">
               <div className="ord-info-header">
                  <span className="ord-info-id">#{code}</span>
                  <span className={`ord-status-pill ${status}`}>
                     <span className="ord-status-dot" />
                     {statusLabel(status, t)}
                  </span>
               </div>
               <div className="ord-info-name">{name}</div>
               <div className="ord-info-meta">
                  {order.createdAt && (
                     <span className="ord-meta-chip">
                        <IcoCalendar />
                        {order.createdAt.substring(0, 10)}
                     </span>
                  )}
                  {order.table_name && (
                     <span className="ord-meta-chip">
                        <IcoPin /> {order.table_name}
                     </span>
                  )}
                  {order.groups && (
                     <span className="ord-meta-chip">
                        <IcoBox2 />
                        {t('checkout.productsCount', { count: order.groups.length })}
                     </span>
                  )}
               </div>
            </div>

            {/* right */}
            <div className="ord-right">
               <div>
                  <div className="ord-price">{fmt(order.total)}</div>
                  <div className="ord-price-tax">{t('orders.taxIncluded')}</div>
               </div>
               <button
                  className={`ord-toggle-btn${isOpen ? ' open' : ''}`}
                  onClick={() => onToggle(uid)}
               >
                  {isOpen ? t('common.hide') : t('common.seeMore')} <IcoChevron />
               </button>
            </div>
         </div>

         {/* detail panel */}
         <div className={`ord-detail${isOpen ? ' open' : ''}`}>
            <div className="ord-detail-inner">

               {/* LEFT: productos + timeline */}
               <div>
                  <div className="ord-detail-title">{t('orders.orderedProducts')}</div>
                  <div className="ord-items">
                     {(order.groups || []).map((item, i) => (
                        <div className="ord-item" key={i}>
                           <div className="ord-item-img">
                              {item.img
                                 ? <img src={buildCloudinaryImageUrl({ url: item.img, width: 300, quality: "auto", type: "image" }) || undefined} alt={item.name} />
                                 : <div style={{ width: '100%', height: '100%', background: 'var(--bg-2)', display: 'grid', placeItems: 'center', color: 'var(--text-3)', fontSize: '.9rem' }}><IcoReceipt /></div>
                              }
                           </div>
                           <div className="ord-item-body">
                              <div className="ord-item-name">{item.name}</div>
                              <div className="ord-item-desc">
                                 {item.segments?.map(s => s.item?.name || s.name).filter(Boolean).join(' · ')}
                                 {item.note ? ` · ${t('orders.noteLabel', { note: item.note })}` : ''}
                              </div>
                           </div>
                           <span className="ord-item-qty">×{item.amount}</span>
                           <span className="ord-item-price">{fmt(item.price)}</span>
                        </div>
                     ))}
                     {(!order.groups || order.groups.length === 0) && (
                        <p style={{ fontSize: '.78rem', color: 'var(--text-3)', padding: '.5rem' }}>{t('orders.noProductDetails')}</p>
                     )}
                  </div>

                  {/* timeline — basado en status_update_history si existe */}
                  {order.status_update_history?.length > 0 && (
                     <>
                        <div className="ord-detail-title">{t('orders.orderStatus')}</div>
                        <div className="ord-timeline">
                           {order.status_update_history.map((h, i) => (
                              <div className="ord-tl-step done" key={i}>
                                 <div className="ord-tl-dot"><IcoCheck /></div>
                                 <div>
                                    <div className="ord-tl-label">{h.status || h.label || t('orders.updateFallback', { n: i + 1 })}</div>
                                    <div className="ord-tl-time">{h.createdAt ? h.createdAt.substring(0, 16).replace('T', ' ') : '—'}</div>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </>
                  )}
               </div>

               {/* RIGHT: resumen + acciones */}
               <div>
                  <div className="ord-detail-title">{t('orders.paymentSummary')}</div>
                  <div className="ord-summary">
                     <div className="ord-sum-row">
                        <span>{t('cart.subtotal')}</span>
                        <span>{fmt(order.subtotal)}</span>
                     </div>
                     <div className="ord-sum-row">
                        <span>{t('orders.tax')}</span>
                        <span>{fmt(order.tax)}</span>
                     </div>
                     {order.discount > 0 && (
                        <div className="ord-sum-row">
                           <span>{t('orders.discount')}</span>
                           <span style={{ color: '#EF4444' }}>−{fmt(order.discount)}</span>
                        </div>
                     )}
                     <div className="ord-sum-row">
                        <span>{t('checkout.shippingLabel')}</span>
                        <span className="ord-sum-brand">
                           {order.delivery?.value > 0 ? fmt(order.delivery.value) : t('checkout.free')}
                        </span>
                     </div>
                     <div className="ord-sum-row total">
                        <span>{t('cart.total')}</span>
                        <span>{fmt(order.total)}</span>
                     </div>
                  </div>

                  {/* acciones */}
                  <div className="ord-detail-actions">
                     {status !== 'anulado' && (

                        <>

                           {/* <button className="ord-btn-invoice"
                              onClick={() => { onNavigate(order); onToast('Abriendo detalle de orden…'); }}>
                              <IcoReorder /> Ver detalle
                           </button> */}
                        </>

                     )}
                     {status === 'review' || status === 'pendiente' ? (
                        <button className=" ord-btn-reorder"
                           onClick={() => { onShowModal(order); onToast(t('orders.openingInfo')); }}>
                           <IcoReceipt /> {t('orders.viewDetail')}
                        </button>
                     ) : (
                        <>

                           {/* <button className="ord-btn-invoice"
                              onClick={() => onToast(`Descargando comprobante #${code}…`)}>
                              <IcoDownload /> Comprobante
                           </button> */}

                        </>

                     )}
                  </div>
               </div>

            </div>
         </div>
      </div>
   );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT — lógica 100% original
══════════════════════════════════════════════════════════════ */
export const Orders = () => {
   const { t } = useTranslation();
   const navigate = useNavigate();
   const [modalUser, setModalUser] = React.useState(false);
   const [modalShow, setModalShow] = React.useState(false);

   /* inject CSS once */
   useEffect(() => {
      const id = "ord-v2-styles";
      if (!document.getElementById(id)) {
         const el = document.createElement("style");
         el.id = id; el.textContent = CSS;
         document.head.appendChild(el);
      }
   }, []);

   /* ════════════════════════════════════════════════════════
      LÓGICA ORIGINAL — 100% INTACTA
   ════════════════════════════════════════════════════════ */
   const [business_code, setbusiness_code] = useState(() => {
      if (localStorage.getItem("business") != "" && localStorage.getItem("business") != undefined)
         return JSON.parse(localStorage.getItem("business")).code;
   });
   const [branchoffice_code, setbranchoffice_code] = useState(() => {
      if (localStorage.getItem("branchoffice") != "" && localStorage.getItem("branchoffice") != undefined)
         return JSON.parse(localStorage.getItem("branchoffice")).code;
   });
   const [machine_code_table, setmachine_code_table] = useState(() => {
      if (localStorage.getItem("machine") != "" && localStorage.getItem("machine") != undefined)
         return JSON.parse(localStorage.getItem("machine")).code;
   });
   const [guestClient, setGuestClient] = useState(() => {
      if (localStorage.getItem('guestClient') != null && localStorage.getItem('guestClient') != undefined && localStorage.getItem('guestClient') != '')
         return localStorage.getItem('guestClient');
      return null;
   });
   const [selectedOrder, setSelectedOrder] = useState({ groups: [], combos: [], total: 0, code: "" });
   const [minDate, setMinDate] = useState(moment(new Date()).tz('America/Bogota').format('YYYY-MM-DD') + 'T00:00:00.000+00:00');
   const [maxDate, setMaxDate] = useState(moment(new Date()).tz('America/Bogota').format('YYYY-MM-DD') + 'T23:59:59.000+00:00');
   const [temporalOrdersDeletedData, setTemporalOrdersDeletedData] = useState([]);
   const [temporalOrdersSuccessData, setTemporalOrdersSuccessData] = useState([]);
   const [eventHandler, setEventHandler] = useState(0);
   const [orders, setOrders] = useState([]);
   const [OrdersPending, setOrdersPending] = useState([]);

   let table = localStorage.getItem('table');
   let token = undefined;
   let machine = localStorage.getItem('machine');
   let branchoffice = localStorage.getItem('branchoffice');

   if (machine !== null && machine !== undefined && machine !== '' && branchoffice !== null && branchoffice !== undefined && branchoffice !== '') {
      machine = JSON.parse(localStorage.getItem('machine'));
      branchoffice = JSON.parse(localStorage.getItem('branchoffice'));
   }
   if (table != null && table != undefined && table != '') {
      table = JSON.parse(localStorage.getItem('table'));
   } else { table = 0; }

   const [tableInfo, setTableInfo] = useState(0);

   const resolveGuestLastOrder = async () => {
      setOrders([]);
      setOrdersPending([]);

      const rawLastOrder = localStorage.getItem("lastorderguest");
      if (!rawLastOrder) return;

      const lastOrder = JSON.parse(rawLastOrder);

      const guestTransactionCode =
         lastOrder?.transaction_code ||
         lastOrder?.code ||
         lastOrder?.registered_transaction_code ||
         "";

      if (!guestTransactionCode) {
         // If there is no reliable identifier, keep it as pending fallback
         setOrdersPending([lastOrder]);
         setSelectedTab("catogire2");
         return;
      }

      try {
         // First, verify if it already exists as a billed/final order
         const billedResponse = await OrdersFetchAPI.getTransactionByCode(
            guestTransactionCode,
            business_code,
            branchoffice_code,
            token
         );

         if (billedResponse?.data?.order) {
            setOrders([billedResponse.data.order]);
            setOrdersPending([]);
            setSelectedTab("catogire1");
            return;
         }
      } catch (err) {
         // Not found in billed orders, continue to pending fallback
         console.log("Guest order not found in billed orders", err);
      }

      // If it was not found as billed, show it as pending
      setOrders([]);
      setOrdersPending([lastOrder]);
      setSelectedTab("catogire2");
   };

   const getCurrentClientForOrders = async (clientHint) => {
      const clientId = clientHint?.id || JSON.parse(localStorage.getItem('client') || 'null')?.id;
      const response = await ClientsFetchAPI.getCurrentClient(token, clientId);
      const currentClient = normalizeClient(response.data);

      if (!currentClient?.id) {
         clearClientSession();
         throw new Error("Cliente no autorizado");
      }

      localStorage.setItem('client', JSON.stringify(currentClient));
      localStorage.setItem('guestClient', 0);
      return currentClient;
   };

   const getClientOrdersPayload = (response) => {
      return response?.data?.data || response?.data || {};
   };


   const getOrdersByBranchByTableAndUser = async (client) => {
      setOrders([]);
      setOrdersPending([]);

      if (guestClient !== 0 && guestClient !== "0" && guestClient !== null && guestClient !== undefined) {
         await resolveGuestLastOrder();
      } else {
         let table_id = 0;
         if (table != 0) table_id = table.id;
         console.log("Table:   ======", table_id);
         try {
            await getCurrentClientForOrders(client);
            const response = await OrdersFetchAPI.getCurrentClientOrders({
               business_code,
               branchoffice_code,
               minDate,
               maxDate,
               payment_method_id: 0,
               user_id: 0,
               machine_id: machine?.id || 0,
               table_id,
            }, token);
            const payload = getClientOrdersPayload(response);
            setOrdersPending(payload.temp_orders || payload.pending_orders || []);
            setOrders(payload.orders || []);
         } catch (err) {
            setOrders([]);
            setOrdersPending([]);
            console.log(err);
         }
      }
   };

   const getOrderByTransactionCode = async (order) => {
      try {
         const response = await OrdersFetchAPI.getTransactionByCode(order.transaction_code, business_code, branchoffice_code, token);
         setSelectedOrder(response.data.order);
         console.log(response.data.order);
         navigate(`/order/${response.data.order.code}`);
      } catch (err) { console.log(err); }
   };

   const getPreOrdertablet = async () => {
      try {
         const response = await PreOrderFetchAPI.getTablePreOrder(table.table_code, business_code, branchoffice_code, token);
         let ordersFilter = response.data.orders.filter((order) => order.table_password && order.table_password == table.password);
         setOrdersPending(ordersFilter);
         console.log(ordersFilter);
      } catch (err) { setOrdersPending([]); console.log(err); }
   };

   const getDeleteTemporal = async (client) => {
      let table_code = 0, client_id = 0;
      try {
         if (client !== undefined) {
            const currentClient = await getCurrentClientForOrders(client);
            client_id = currentClient.id;
         } else { table_code = table.table_code; }
         const response = await PreOrderFetchAPI.getDeletedTransactionReport(business_code, branchoffice_code, minDate, maxDate, 0, token, table_code, client_id);
         let dataOrders = response.data.temp_orders;
         let dataSuccess = [], dataDeleted = [];
         dataOrders.forEach((item) => {
            if (item.deleted == true) { dataDeleted.push(item); } else { dataSuccess.push(item); }
         });
         setTemporalOrdersDeletedData(dataDeleted);
         setTemporalOrdersSuccessData(dataSuccess);
      } catch (err) { console.log(err); setTemporalOrdersDeletedData([]); }
   };

   const getDeleteOrderTemporalById = async (_id) => {
      try {
         const response = await PreOrderFetchAPI.getDeletedTransactionById(_id, token);
         setModalShow(true);
         setSelectedOrder(response.data.deleted_order);
         console.log(response.data);
      } catch (err) { console.log(err); }
   };

   const emptyOrders = () => (
      <div className="ord-empty">
         <div className="ord-empty-ico">📦</div>
         <p className="ord-empty-title">{t('orders.emptyTitle')}</p>
         <p className="ord-empty-sub">{t('orders.emptySubtitle')}</p>
      </div>
   );

   useEffect(() => {
      if (table == 0) {
         if (localStorage.getItem('client') !== null && localStorage.getItem('client') !== undefined && localStorage.getItem('client') !== '') {
            let client = JSON.parse(localStorage.getItem('client'));
            getOrdersByBranchByTableAndUser(client);
            getDeleteTemporal(client);
         } else { setModalUser(true); }
         setTableInfo(0);
      } else {
         getPreOrdertablet();
         getOrdersByBranchByTableAndUser();
         getDeleteTemporal();
         setTableInfo(table);
      }
      setEventHandler(0);
   }, [eventHandler]);

   const [selectedTab, setSelectedTab] = useState("catogire1");
   const handleTabChange = (event) => { setSelectedTab(event.target.id); };

   useEffect(() => {
      if (localStorage.getItem("branchoffice") == null || localStorage.getItem("branchoffice") == undefined || localStorage.getItem("branchoffice") == "") {
         ErrorAlert('', t('menu.mustSelectRestaurant'), 'info', 8000);
         navigate('/home');
      }
   }, []);

   const [temporalOrdersData, setTemporalOrdersData] = useState([]);

   useEffect(() => {
      if (localStorage.getItem('table') !== null && localStorage.getItem('table') !== undefined && localStorage.getItem('table') !== '') {
         const socket = Socket({ "business_code": business_code });
         socket.on(`businessTempOrder_${business_code}`, (newTempOrder) => {
            let selected_branch = localStorage.getItem('branchoffice_selected');
            console.log(newTempOrder);
            setOrders((prevTempOrders) => {
               if (newTempOrder.table_password && newTempOrder.table_code == table.table_code && newTempOrder.table_password == table.password) {
                  return [newTempOrder, ...prevTempOrders];
               } else { return prevTempOrders; }
            });
         });
         socket.on(`deleteBusinessTempOrder_${business_code}`, (deletedTempOrder) => {
            console.log("Eliminada"); console.log(deletedTempOrder);
         });
         return () => { socket.disconnect(); };
      }
   }, [business_code]);

   const [loading, setLoading] = useState(true);
   /* ════════════════════════════════════════════════════════
      FIN LÓGICA ORIGINAL
   ════════════════════════════════════════════════════════ */

   /* ── UI state del nuevo diseño ── */
   const [openDetailId, setOpenDetailId] = useState(null);
   const [search, setSearch] = useState('');
   const [toast, setToast] = useState({ visible: false, message: '' });
   const toastTimer = useRef(null);

   const showToast = (msg) => {
      clearTimeout(toastTimer.current);
      setToast({ visible: true, message: msg });
      toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 2800);
   };

   const handleToggle = (code) => {
      setOpenDetailId(prev => {
         const next = prev === code ? null : code;
         if (next) {
            setTimeout(() => {
               const el = document.getElementById(`ord-${code}`);
               if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 120);
         }
         return next;
      });
   };

   /* datos activos según tab seleccionado */
   const activeOrders = (() => {
      switch (selectedTab) {
         case 'catogire1': return orders;
         case 'catogire2': return OrdersPending;
         case 'catogire3': return temporalOrdersSuccessData;
         case 'catogire4': return temporalOrdersDeletedData;
         default: return orders;
      }
   })();

   /* filtro de búsqueda */
   const filtered = activeOrders.filter(o => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      const code = (o.transaction_code || o.code || '').toLowerCase();
      const name = (o.name || o.groups?.map(g => g.name).join(' ') || '').toLowerCase();
      return code.includes(q) || name.includes(q);
   });

   /* tabs config */
   const TABS = [
      { id: 'catogire1', label: t('orders.tabInvoiced'), count: orders.length },
      { id: 'catogire2', label: t('orders.tabPending'), count: OrdersPending.length },
      { id: 'catogire3', label: t('orders.tabReady'), count: temporalOrdersSuccessData.length },
      { id: 'catogire4', label: t('orders.tabCancelled'), count: temporalOrdersDeletedData.length },
   ];

   /* navigate handler per tab */
   const handleNavigate = (order) => {
      if (selectedTab === 'catogire1') getOrderByTransactionCode(order);
      else if (selectedTab === 'catogire3' || selectedTab === 'catogire4') getDeleteOrderTemporalById(order._id);
   };

   const handleShowModal = (order) => {
      setModalShow(true);
      setSelectedOrder(order);
   };

   /* ══════════════════════════════════════════════════════
      RENDER
   ══════════════════════════════════════════════════════ */
   return (
      <>
         {loading && <Loader onFinish={() => setLoading(false)} />}

         {!loading && (
            <div className="ecommerce-wrapper">
               <HeaderMain />

               <div className="ord-page">

                  {/* ── PAGE HEADER ── */}
                  <div className="ord-ph">
                     <div className="ord-ph-inner">
                        <div className="ord-ph-left">
                           <p className="ord-ph-eyebrow">{t('orders.historyEyebrow')}</p>
                           <h1 className="ord-ph-title">
                              {guestClient !== 0 && guestClient !== "0" && guestClient !== null && guestClient !== undefined
                                 ? t('orders.guestLastOrder')
                                 : t('orders.myOrders')}
                           </h1>
                           <p className="ord-ph-sub">{t('orders.subtitle')}</p>
                           <button
                              className="ord-refresh"
                              onClick={() => { setEventHandler(1); AlertSmall(t('orders.ordersUpdated'), 'success'); }}
                           >
                              <IcoRefresh /> {t('orders.refresh')}
                           </button>
                        </div>

                        {/* búsqueda */}
                        <div className="ord-search">
                           <IcoSearch />
                           <input
                              type="text"
                              placeholder={t('orders.searchPlaceholder')}
                              value={search}
                              onChange={e => { setSearch(e.target.value); setOpenDetailId(null); }}
                           />
                        </div>
                     </div>
                  </div>

                  {/* ── TABS ── */}
                  <div className="ord-tabs-wrap">
                     {TABS.map(tab => (
                        <button
                           key={tab.id}
                           className={`ord-tab${selectedTab === tab.id ? ' active' : ''}`}
                           onClick={() => { setSelectedTab(tab.id); setOpenDetailId(null); setSearch(''); }}
                        >
                           {tab.label}
                           <span className="ord-tab-count">{tab.count}</span>
                        </button>
                     ))}
                  </div>

                  {/* ── META ── */}
                  <p className="ord-meta">
                     {t('orders.resultsFound', { count: filtered.length })}
                  </p>

                  {/* ── LIST ── */}
                  <div className="ord-list">
                     {filtered.length === 0 ? emptyOrders() : filtered.map((order, i) => {
                        const uid = order._id || order.transaction_code || order.code || String(i);
                        return (
                           <OrderCard
                              key={uid}
                              order={order}
                              tab={selectedTab}
                              isOpen={openDetailId === uid}
                              onToggle={handleToggle}
                              onNavigate={handleNavigate}
                              onShowModal={handleShowModal}
                              onToast={showToast}
                              style={{ animationDelay: `${i * 0.04}s` }}
                           />
                        );
                     })}
                  </div>

               </div>
            </div>
         )}


         <InfoOrdersModal
            modalShow={modalShow}
            setModalShow={setModalShow}
            selectedOrder={selectedOrder}
         />

         <Toast message={toast.message} visible={toast.visible} />
      </>
   );
};
